import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import Button from '../../../components/Button';
import SignOutLink from '../../../components/SignOutLink/SignOutLink';
import PlatefullAILogo from '../../../images/PlatefullAILogoNB.svg';
import styles from '../Onboard.module.css';
import { userClientAuthorizationService, UserClientAuthorization } from '../../../services/userClientAuthorizationService';

interface OAuthConsentStepProps {
  onComplete: (isExistingAuth?: boolean) => void;
  onBack: () => void;
  oauthParams: {
    client_id: string;
    redirect_uri: string;
    state?: string;
    scope?: string;
    code_challenge?: string;
    code_challenge_method?: string;
  };
}

const OAuthConsentStep: React.FC<OAuthConsentStepProps> = ({
  onComplete,
  onBack,
  oauthParams
}) => {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingAuthorization, setIsCheckingAuthorization] = useState(true);
  const [error, setError] = useState<string>('');
  const [authorization, setAuthorization] = useState<UserClientAuthorization | null>(null);

  // Parse scopes for display
  const scopes = oauthParams.scope ? oauthParams.scope.split(' ') : [];
  const scopeDescriptions: { [key: string]: string } = {
    'dietary_preferences:read': 'Read your dietary preferences and restrictions',
    'ingredients:read': 'Read your ingredient preferences and allergies',
    'profile:read': 'Read your basic profile information (name, email)'
  };

  // Check for existing authorization when component mounts
  useEffect(() => {
    const checkExistingAuthorization = async () => {
      if (!user) {
        setIsCheckingAuthorization(false);
        return;
      }

      try {
        const existingAuth = await userClientAuthorizationService.getAuthorizationByUserAndClient(
          user.id,
          oauthParams.client_id
        );

        if (existingAuth) {
          setAuthorization(existingAuth);
          
          // If user already has permission and it's active, check if they have complete profile
          if (existingAuth.permission && existingAuth.active) {
            // Check if user has dietary preferences and health concerns
            const hasDietaryPreferences = user?.preferences?.dietary && user.preferences.dietary.length > 0;
            const hasHealthConcerns = user?.preferences?.healthConcerns && user.preferences.healthConcerns.length > 0;
            
            // If user has both dietary preferences and health concerns, redirect immediately
            // Otherwise, continue with onboarding to complete their profile
            if (hasDietaryPreferences && hasHealthConcerns) {
              onComplete(true); // true indicates this is an existing authorization with complete profile
            } else {
              onComplete(false); // false indicates they need to complete onboarding
            }
            return;
          }
        }
      } catch (error) {
        console.error('Error checking existing authorization:', error);
        setError('Failed to check existing permissions. Please try again.');
      } finally {
        setIsCheckingAuthorization(false);
      }
    };

    checkExistingAuthorization();
  }, [user, oauthParams.client_id, onComplete]);

  const handleAllow = async () => {
    if (!user) {
      setError('User not logged in');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      if (authorization) {
        // Update existing authorization
        await userClientAuthorizationService.updateAuthorization(authorization.id, {
          permission: true,
          active: true
        });
      } else {
        // Create new authorization
        await userClientAuthorizationService.createAuthorization({
          user_id: user.id,
          client_id: oauthParams.client_id,
          permission: true,
          active: true
        });
      }

      onComplete(false); // false indicates this is a new authorization
    } catch (err) {
      console.error('Error saving authorization:', err);
      setError('Failed to save permission. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeny = async () => {
    if (!user) {
      setError('User not logged in');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Save the denial in our authorization system
      if (authorization) {
        // Update existing authorization
        await userClientAuthorizationService.updateAuthorization(authorization.id, {
          permission: false,
          active: false
        });
      } else {
        // Create new authorization with denial
        await userClientAuthorizationService.createAuthorization({
          user_id: user.id,
          client_id: oauthParams.client_id,
          permission: false,
          active: false
        });
      }

      // Also call the OAuth deny endpoint
      const formData = new FormData();
      formData.append('client_id', oauthParams.client_id);
      formData.append('redirect_uri', oauthParams.redirect_uri);
      formData.append('state', oauthParams.state || '');

      const response = await fetch('http://localhost:8000/oauth/authorize/deny', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to deny authorization');
      }

      const data = await response.json();
      
      // Close the popup since user denied
      if (window.opener) {
        window.close();
      } else {
        // Fallback: redirect if no parent window
        window.location.href = data.redirect_url;
      }
    } catch (err) {
      console.error('Denial error:', err);
      setError('Failed to process request. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkip = () => {
    // For OAuth flow, we can't really "skip" - we need to either allow or deny
    // But we can treat skip as deny
    handleDeny();
  };

  // Show loading while checking existing authorization
  if (isCheckingAuthorization) {
    return (
      <div className={styles.stepContainer}>
        <div className={styles.stepHeader}>
          <div className={styles.logoContainer}>
            <img src={PlatefullAILogo} alt="PLATEFULL AI Logo" className={styles.logoImage} />
          </div>
          <h2 className={styles.stepTitle}>
            Checking Permissions...
          </h2>
        </div>
        
        <div className={styles.stepDescription}>
          <div className={styles.stepDescriptionText}>
            <p>Checking your existing permissions for this application...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.stepContainer}>
      <div className={styles.stepHeader}>
        <div className={styles.logoContainer}>
          <img src={PlatefullAILogo} alt="PLATEFULL AI Logo" className={styles.logoImage} />
        </div>
        <h2 className={styles.stepTitle}>
          Authorize Application
        </h2>
      </div>

      <div className={styles.stepContent}>
        <div className={styles.appInfo}>
          <h3>This app wants to access your PlatefullAI account</h3>
        </div>

        <div className={styles.permissions}>
          <h4>This application will be able to:</h4>
          <ul>
            {scopes.map((scopeName) => (
              <li key={scopeName}>
                {scopeDescriptions[scopeName] || scopeName}
              </li>
            ))}
          </ul>
        </div>

        <div className={styles.userInfo}>
          <p>Signed in as: <strong>{user?.email}</strong></p>
        </div>

        {authorization && (
          <div className={styles.permissionStatus}>
            <h4>Current Permission Status:</h4>
            <div className={authorization.permission ? styles.permissionGranted : styles.permissionDenied}>
              {authorization.permission ? (
                <p>✅ You have previously granted permission to this application</p>
              ) : (
                <p>❌ You have previously denied permission to this application</p>
              )}
            </div>
            {!authorization.active && (
              <div className={styles.permissionInactive}>
                <p>⚠️ This permission is currently inactive</p>
              </div>
            )}
          </div>
        )}

        {error && (
          <div className={styles.errorMessage}>
            {error}
          </div>
        )}

        <div className={styles.actionPrompt}>
          <p>Click "Allow" to see your PlatefullAI ID active on this website</p>
        </div>

        <div className={styles.buttonContainer}>
          <Button
            onClick={handleDeny}
            disabled={isLoading}
            variant="secondary"
            size="medium"
          >
            {isLoading ? 'Processing...' : 'Deny'}
          </Button>
          <Button
            onClick={handleAllow}
            disabled={isLoading}
            variant="primary"
            size="medium"
          >
            {isLoading ? 'Processing...' : 'Allow'}
          </Button>
        </div>
        
        <SignOutLink showEmail={true} />
      </div>
    </div>
  );
};

export default OAuthConsentStep;
